<script lang="ts">
  import ShareIcon from '$icons/share/ShareIcon.svelte';
  import SocialShareable, { socialCardQueryKey } from '$lib/pages/Hackathon/SocialShareable.svelte';
  import { cn } from '$lib/utils/misc';
  import {
    AccountRoles,
    FEATURE_FLAGS,
    HackathonParticipationStatus,
    type HackathonWithState,
  } from '$shared/lib';
  import XIcon from '$icons/XIcon.svelte';
  import { page } from '$app/stores';
  import { onMount } from 'svelte';
  import { client } from '$lib/api/Client';
  import { useQueryClient } from '@tanstack/svelte-query';
  import type { RetrieveSocialShareableImageResponse } from '$shared/api';
  import { fade } from 'svelte/transition';
  import Button from '../Button.svelte';

  export let hackathon: HackathonWithState;
  export let isDashboard = false;

  let showShareModal = false;
  const queryClient = useQueryClient();

  onMount(async () => {
    if (
      !socialShareableEnabled ||
      hackathon.participationStatus < HackathonParticipationStatus.REGISTERED
    ) {
      return;
    }

    try {
      // Get the social card data
      const socialCard = await queryClient.fetchQuery({
        queryKey: socialCardQueryKey(),
        queryFn: () => client.hackathon.getSocialShareableImage(),
        staleTime: Infinity, // prevent refetch, reuse cache
      });

      // If it hasn't been viewed, show it and mark as viewed
      if (socialCard && socialCard.viewedAt === null) {
        showShareModal = true;

        // Mark as viewed in backend
        await client.hackathon.markSocialImageViewed({ id: socialCard.id });

        // Update cache immediately
        queryClient.setQueryData(
          socialCardQueryKey(),
          (oldData: RetrieveSocialShareableImageResponse | undefined) =>
            oldData ? { ...oldData, viewedAt: new Date() } : undefined,
        );
      }
    } catch (error) {
      console.error('Error handling social card:', error);
    }
  });

  $: socialShareableEnabled =
    $page.data.featureFlags?.[FEATURE_FLAGS.SOCIAL_SHAREABLE_IMAGE] ||
    $page.data.user.accountRoles.includes(AccountRoles.ADMIN);
</script>

<div class={cn('relative overflow-hidden rounded-sm', $$props.class)}>
  {#if socialShareableEnabled && $page.data.hackathon.participationStatus >= HackathonParticipationStatus.REGISTERED}
    <div class="bg-mint-dark-3 overflow-hidden rounded-t">
      <div class="bg-gray-dark-2 flex items-center justify-between gap-2 px-4 py-2">
        <span
          class={cn(
            'text-mint-dark-11 font-architekt font-medium',
            isDashboard ? 'text-xs md:text-sm' : 'text-xs',
          )}
        >
          Let others know you've entered the arena! 📣
        </span>
        <Button
          class={`text-mint-dark-12 flex items-center gap-1 rounded bg-none transition-all duration-200 hover:scale-105 ${
            isDashboard ? 'p-1 md:p-2' : 'p-1'
          }`}
          on:click={() => (showShareModal = true)}
        >
          <span
            class={`font-architekt font-medium ${isDashboard ? 'text-xs md:text-sm' : 'text-xs'}`}
          >
            Share
          </span>
          <ShareIcon class={` ${isDashboard ? 'h-4 w-4 md:h-5 md:w-5' : 'h-4 w-4'}`} />
        </Button>
      </div>
    </div>
  {/if}
  <a href={hackathon.landingPageUrl} target="_blank">
    {#if hackathon.participationStatus >= HackathonParticipationStatus.REGISTERED && !isDashboard}
      <div
        class="relative flex h-auto w-full flex-col items-center justify-center bg-center bg-no-repeat transition-all duration-300 hover:scale-[1.03]"
      >
        <img
          src={hackathon.smallBannerImageUrl}
          alt={hackathon.description}
          class="h-auto min-h-[50px] w-full object-cover"
        />
      </div>
    {:else}
      <div
        class="relative flex h-auto w-full flex-col items-center justify-center bg-center bg-no-repeat transition-all duration-300 hover:scale-[1.03]"
      >
        <img
          src={hackathon.largeBannerImageUrl}
          alt={hackathon.description}
          class={!isDashboard
            ? 'h-auto min-h-[75px] w-full object-cover'
            : 'h-auto min-h-[75px] w-full object-cover'}
        />
      </div>
    {/if}
  </a>
  <!-- {#if showCountDown && hackathon.phase !== HackathonPhase.WINNERS_ANNOUNCED}
    <div class="right-2 top-1/2 flex w-full -translate-y-1/2 sm:absolute sm:block sm:w-40">
      <CountDown {hackathon} />
    </div>
  {/if} -->
</div>

{#if showShareModal}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div
    transition:fade={{ duration: 200 }}
    class="fixed inset-0 z-[101] flex flex-col items-center justify-center bg-black/80 p-4"
    on:click={() => (showShareModal = false)}
  >
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
    <div
      transition:fade={{ duration: 250, delay: 100 }}
      class="border-gray-dark-5 bg-gray-dark-1 relative z-[102] flex w-full max-w-[616px] flex-col overflow-hidden rounded border-2 border-solid shadow-lg"
      on:click|stopPropagation
    >
      <!-- Modal Header -->
      <div
        class="border-gray-dark-5 px-lg py-sm flex items-center justify-between gap-2 border-b-2"
      >
        <h2 class="font-architekt text-gray-dark-12 md:text-md text-xs leading-5 sm:text-sm">
          Let others know you've entered the arena!
        </h2>
        <button on:click={() => (showShareModal = false)}>
          <XIcon class="text-gray-dark-11" />
        </button>
      </div>
      <!-- Modal Body -->
      <SocialShareable class="rounded-t-none" modalMode />
    </div>
  </div>
{/if}
